import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, LayoutGrid, Expand } from "lucide-react";
import api from "../api";
import { useLang, fmtPrice, statusKey } from "../i18n";
import AcquirePanel from "../components/AcquirePanel";

export default function Works() {
  const { t, loc, lang } = useLang();
  const [works, setWorks] = useState([]);
  const [series, setSeries] = useState("all");
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState("view");
  const [origin, setOrigin] = useState("50% 50%");
  const [acquire, setAcquire] = useState(false);

  useEffect(() => {
    api.get("/artworks").then((r) => setWorks(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (series === "all" ? works : works.filter((w) => w.series === series)),
    [works, series]
  );
  const current = filtered[Math.min(idx, filtered.length - 1)];
  const seriesList = useMemo(() => {
    const map = new Map();
    works.forEach((w) => map.set(w.series, loc(w, "series")));
    return [...map.entries()];
  }, [works, loc]);

  const go = useCallback(
    (d) => setIdx((i) => (filtered.length ? (i + d + filtered.length) % filtered.length : 0)),
    [filtered.length]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (mode !== "view") return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, mode]);

  useEffect(() => setIdx(0), [series]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <div data-testid="works-page" className="pt-16 min-h-screen flex flex-col">
      {/* Toolbar */}
      <div className="px-6 sm:px-12 lg:px-20 py-5 flex flex-wrap items-center gap-x-8 gap-y-4 border-b" style={{ borderColor: "#E5E2DC" }}>
        <h1 className="font-serif-display text-3xl font-light mr-2" data-testid="works-title">
          {t("works.title")}
          <sup className="font-mono-meta text-[10px] tracking-[0.2em] text-[#8C8983] ml-2">
            {filtered.length} {t("works.count")}
          </sup>
        </h1>
        <div className="flex flex-wrap gap-2" data-testid="series-filters">
          {["all", ...seriesList.map(([k]) => k)].map((k) => (
            <button
              key={k}
              data-testid={`filter-${k}`}
              onClick={() => setSeries(k)}
              className={`font-mono-meta text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors duration-300 ${
                series === k
                  ? "bg-[#121212] text-[#FAF9F6] border-[#121212]"
                  : "border-[#C5C0B7] text-[#5A5853] hover:border-[#121212] hover:text-[#121212]"
              }`}
            >
              {k === "all" ? t("works.all") : seriesList.find(([sk]) => sk === k)?.[1]}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button
            data-testid="mode-view-btn"
            onClick={() => setMode("view")}
            className={`p-2 border transition-colors ${mode === "view" ? "bg-[#121212] text-[#FAF9F6] border-[#121212]" : "border-[#C5C0B7]"}`}
            aria-label={t("works.view")}
          >
            <Expand size={15} />
          </button>
          <button
            data-testid="mode-grid-btn"
            onClick={() => setMode("grid")}
            className={`p-2 border transition-colors ${mode === "grid" ? "bg-[#121212] text-[#FAF9F6] border-[#121212]" : "border-[#C5C0B7]"}`}
            aria-label={t("works.grid")}
          >
            <LayoutGrid size={15} />
          </button>
        </div>
      </div>

      {mode === "view" ? (
        <div className="flex-1 flex flex-col" data-testid="immersive-viewer">
          {/* Stage */}
          <div className="relative flex-1 flex items-center justify-center px-6 sm:px-24 py-10 min-h-[58vh]">
            <button
              data-testid="viewer-prev-btn"
              onClick={() => go(-1)}
              className="absolute left-4 sm:left-10 z-10 p-3 border border-[#C5C0B7] rounded-full hover:bg-[#121212] hover:text-[#FAF9F6] hover:border-[#121212] transition-colors"
              aria-label={t("detail.prev")}
            >
              <ArrowLeft size={18} />
            </button>
            <AnimatePresence mode="wait">
              {current && (
                <motion.figure
                  key={current.slug}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                  className="zoomable overflow-hidden cursor-crosshair max-h-full"
                  onMouseMove={onMove}
                >
                  <img
                    src={current.image}
                    alt={loc(current, "title")}
                    className="zoom-img max-h-[62vh] w-auto max-w-full object-contain frame-shadow"
                    style={{ transformOrigin: origin }}
                    data-testid="viewer-image"
                  />
                </motion.figure>
              )}
            </AnimatePresence>
            <button
              data-testid="viewer-next-btn"
              onClick={() => go(1)}
              className="absolute right-4 sm:right-10 z-10 p-3 border border-[#C5C0B7] rounded-full hover:bg-[#121212] hover:text-[#FAF9F6] hover:border-[#121212] transition-colors"
              aria-label={t("detail.next")}
            >
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Caption bar */}
          {current && (
            <div
              className="border-t px-6 sm:px-12 lg:px-20 py-5 flex flex-wrap items-center gap-x-10 gap-y-4"
              style={{ borderColor: "#E5E2DC", background: "#F3F1EC" }}
              data-testid="viewer-caption"
            >
              <div className="min-w-0">
                <p className="font-serif-display italic text-2xl truncate" data-testid="viewer-artwork-title">
                  {loc(current, "title")}
                </p>
                <p className="font-mono-meta text-[11px] uppercase tracking-[0.15em] text-[#8C8983] mt-1">
                  {loc(current, "technique")} · {current.dimensions} · {current.year}
                </p>
              </div>
              <span
                className="font-mono-meta text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border"
                style={
                  current.status === "available"
                    ? { color: "#121212", borderColor: "#121212" }
                    : { color: "#8C8983", borderColor: "#C5C0B7" }
                }
                data-testid="viewer-status"
              >
                {t(statusKey(current.status))}
              </span>
              {current.status !== "sold" && (
                <p className="font-serif-display text-2xl" data-testid="viewer-price">
                  {fmtPrice(current.price_eur, lang)}
                </p>
              )}
              <div className="ml-auto flex gap-3">
                <Link
                  to={`/oeuvres/${current.slug}`}
                  data-testid="viewer-details-link"
                  className="px-5 py-3 font-mono-meta text-[11px] uppercase tracking-[0.18em] border border-[#121212] hover:bg-[#121212] hover:text-[#FAF9F6] transition-colors"
                >
                  {t("works.details")}
                </Link>
                {current.status === "available" && (
                  <button
                    data-testid="viewer-acquire-btn"
                    onClick={() => setAcquire(true)}
                    className="px-5 py-3 font-mono-meta text-[11px] uppercase tracking-[0.18em] text-[#FAF9F6] hover:opacity-85 transition-opacity"
                    style={{ background: "#C83E2B" }}
                  >
                    {t("detail.acquire")}
                  </button>
                )}
              </div>
            </div>
          )}
          <p className="px-6 sm:px-12 lg:px-20 py-3 font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]" style={{ background: "#F3F1EC" }}>
            {t("works.hint")}
          </p>
        </div>
      ) : (
        /* Index grid */
        <div className="px-6 sm:px-12 lg:px-20 py-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14" data-testid="works-grid">
          {filtered.map((w, i) => (
            <motion.div
              key={w.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.6), duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                className="group block w-full text-left"
                data-testid={`grid-work-${w.slug}`}
                onClick={() => {
                  setIdx(i);
                  setMode("view");
                }}
              >
                <div className="overflow-hidden mb-4" style={{ background: "#F3F1EC" }}>
                  <img
                    src={w.image}
                    alt={loc(w, "title")}
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="font-serif-display italic text-lg leading-tight">{loc(w, "title")}</p>
                <p className="font-mono-meta text-[10px] uppercase tracking-[0.15em] text-[#8C8983] mt-1">
                  {w.dimensions} · {w.year} · {w.status === "sold" ? t("works.sold") : fmtPrice(w.price_eur, lang)}
                </p>
              </button>
            </motion.div>
          ))}
        </div>
      )}

      <AcquirePanel artwork={current} open={acquire} onClose={() => setAcquire(false)} />
    </div>
  );
}
